import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../../components/ConfirmModal';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchEntradaMaterialById, updateEntradaMaterial, deleteEntradaMaterial, fetchProdutos } from '../../../services/entradaMaterialService';

const initialState = {
  dataEntrada: '',
  quantidade: '',
  produto: '',
  observacao: '',
  tipo: 'Normal'
};

const EntradaMaterialEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const [loadingProdutos, setLoadingProdutos] = useState(true);

  useEffect(() => {
    const loadEntrada = async () => {
      try {
        setLoading(true);
        const entrada = await fetchEntradaMaterialById(id);
        setForm({
          dataEntrada: entrada.dataEntrada || '',
          quantidade: entrada.quantidade || '',
          produto: entrada.produto || '',
          observacao: entrada.observacao || '',
          tipo: entrada.tipo || 'Normal'
        });
        setError('');
      } catch (err) {
        setError('Erro ao carregar entrada de material: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    const loadProdutos = async () => {
      try {
        const data = await fetchProdutos();
        setProdutos(data);
      } catch (err) {
        console.error('Erro ao carregar produtos:', err);
      } finally {
        setLoadingProdutos(false);
      }
    };

    if (id) {
      loadEntrada();
    }
    loadProdutos();
  }, [id]);

  const validate = () => {
    if (!form.dataEntrada.trim()) return 'A data de entrada é obrigatória.';
    if (!form.quantidade || form.quantidade <= 0) return 'A quantidade deve ser maior que zero.';
    if (!form.produto || form.produto <= 0) return 'O produto é obrigatório.';
    return '';
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const confirmUpdate = async () => {
    setShowConfirm(false);
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      await updateEntradaMaterial(id, form);
      setSuccess(true);
      setTimeout(() => navigate('/estoque/entrada'), 1200);
    } catch (err) {
      setError('Erro ao atualizar entrada de material: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">Carregando...</div>
        </div>
      </div>
    );
  }

  if (deleteSuccess) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-green-600">Entrada de material excluída com sucesso!</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Entrada de Material</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Data de Entrada *</label>
            <input
              type="date"
              name="dataEntrada"
              value={form.dataEntrada}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Quantidade *</label>
            <input
              type="number"
              name="quantidade"
              value={form.quantidade}
              onChange={handleChange}
              required
              min="1"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="Quantidade"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Produto *</label>
            {loadingProdutos ? (
              <div className="w-full border rounded px-3 py-2 text-gray-500">
                Carregando produtos...
              </div>
            ) : (
              <select
                name="produto"
                value={form.produto}
                onChange={handleChange}
                required
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                <option value="">Selecione um produto</option>
                {produtos.map(produto => (
                  <option key={produto.idProduto} value={produto.idProduto}>
                    {produto.nome}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Tipo *</label>
            <select
              name="tipo"
              value={form.tipo}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="Normal">Normal</option>
              <option value="Troca">Troca</option>
              <option value="Devolução">Devolução</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Observação</label>
          <textarea
            name="observacao"
            value={form.observacao}
            onChange={handleChange}
            rows="3"
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Observações adicionais (opcional)"
          />
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {saving && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Entrada de material atualizada com sucesso!</div>}

        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={() => navigate('/estoque/entrada')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={handleDelete} 
            disabled={deleting}
           aria-label="Excluir">
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>          
          <button 
            type="submit" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
           aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>

        </div>
      </form>

      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Alteração"
        message="Deseja realmente atualizar esta entrada de material?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={confirmUpdate}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir esta entrada de material? Esta ação não poderá ser desfeita."
        confirmType="delete"
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          setDeleting(true);
          setError('');
          
          try {
            await deleteEntradaMaterial(id);
            setDeleteSuccess(true);
            setTimeout(() => navigate('/estoque/entrada'), 1200);
          } catch (err) {
            setError('Erro ao excluir entrada de material: ' + err.message);
            setDeleting(false);
          }
        }}
        danger
      />
    </div>
  );
};

export default EntradaMaterialEdit;
